import { report as reportMessages } from './validationMessages';

export const VISIT_REPORT_PDF_ERROR_CODES = {
  EMPTY_PREVIEW: 'EMPTY_PREVIEW',
  BLOCKED: 'BLOCKED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  TIMEOUT: 'TIMEOUT',
  CANCELLED: 'CANCELLED',
  SERVER_ERROR: 'SERVER_ERROR',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  GENERIC: 'GENERIC',
};

async function readBlobAsText(blob) {
  if (!blob) return '';
  try {
    return await blob.text();
  } catch (_) {
    return '';
  }
}

function tryParseJson(text) {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return null;
  }
}

function isWafBlockPayload(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.attack_ID || data.attack_id) return true;
  if (data.page_title === 'Web Page Blocked!') return true;
  return false;
}

/**
 * Parse axios error response body (often a Blob when responseType is blob).
 */
export async function parseVisitReportPdfErrorBody(err) {
  const data = err?.response?.data;
  if (data == null) return null;
  if (typeof data === 'object' && !(data instanceof Blob)) return data;
  if (data instanceof Blob) {
    const text = await readBlobAsText(data);
    return tryParseJson(text);
  }
  if (typeof data === 'string') return tryParseJson(data);
  return null;
}

/**
 * Classify a Visit Report PDF failure for message mapping.
 */
export async function classifyVisitReportPdfError(err) {
  if (err?.code === 'EMPTY_PREVIEW' || err?.message === 'Visit Report preview HTML is empty.') {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.EMPTY_PREVIEW };
  }
  if (err?.code === 'ECONNABORTED' || err?.message?.toLowerCase?.().includes('timeout')) {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.TIMEOUT };
  }
  if (err?.code === 'INVALID_PDF_RESPONSE') {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.INVALID_RESPONSE };
  }
  if (err?.code === 'BLOCKED_RESPONSE') {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.BLOCKED };
  }

  const status = err?.response?.status;
  const body = await parseVisitReportPdfErrorBody(err);

  if (isWafBlockPayload(body)) {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.BLOCKED };
  }
  if (status === 413) {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.PAYLOAD_TOO_LARGE };
  }
  if (status === 499) {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.CANCELLED };
  }
  if (status >= 500) {
    const serverMessage = body?.message || body?.error || null;
    return { code: VISIT_REPORT_PDF_ERROR_CODES.SERVER_ERROR, serverMessage };
  }
  if (status === 400 && body?.error) {
    return { code: VISIT_REPORT_PDF_ERROR_CODES.EMPTY_PREVIEW, serverMessage: body.error };
  }

  return { code: VISIT_REPORT_PDF_ERROR_CODES.GENERIC };
}

/**
 * Map classified error to user-facing validation message.
 */
export function resolveVisitReportPdfErrorMessage(err, classification) {
  const code = classification?.code ?? VISIT_REPORT_PDF_ERROR_CODES.GENERIC;
  switch (code) {
    case VISIT_REPORT_PDF_ERROR_CODES.EMPTY_PREVIEW:
      return reportMessages.pdfGenerationEmptyPreview;
    case VISIT_REPORT_PDF_ERROR_CODES.BLOCKED:
      return reportMessages.pdfGenerationBlocked;
    case VISIT_REPORT_PDF_ERROR_CODES.PAYLOAD_TOO_LARGE:
      return reportMessages.pdfGenerationPayloadTooLarge;
    case VISIT_REPORT_PDF_ERROR_CODES.TIMEOUT:
      return reportMessages.pdfGenerationTimeout;
    case VISIT_REPORT_PDF_ERROR_CODES.CANCELLED:
      return reportMessages.pdfGenerationServerError;
    case VISIT_REPORT_PDF_ERROR_CODES.SERVER_ERROR: {
      const detail = classification?.serverMessage;
      if (detail && typeof detail === 'string' && detail.trim()) {
        return `${reportMessages.pdfGenerationServerError} (${detail.trim()})`;
      }
      return reportMessages.pdfGenerationServerError;
    }
    case VISIT_REPORT_PDF_ERROR_CODES.INVALID_RESPONSE:
      return reportMessages.pdfGenerationInvalidResponse;
    default:
      return reportMessages.pdfGenerationFailed;
  }
}

/**
 * Verify blob begins with PDF magic bytes.
 */
export async function assertVisitReportPdfBlob(blob) {
  if (!(blob instanceof Blob)) {
    const e = new Error('Invalid PDF response');
    e.code = 'INVALID_PDF_RESPONSE';
    throw e;
  }
  const header = await blob.slice(0, 5).text();
  if (header.startsWith('%PDF')) return blob;

  const previewText = await blob.slice(0, 512).text();
  const json = tryParseJson(previewText);
  if (isWafBlockPayload(json)) {
    const e = new Error('PDF generation blocked');
    e.code = 'BLOCKED_RESPONSE';
    throw e;
  }

  const e = new Error('Invalid PDF response');
  e.code = 'INVALID_PDF_RESPONSE';
  throw e;
}

/**
 * One-shot helper for toast display.
 */
export async function getVisitReportPdfErrorMessage(err) {
  const classification = await classifyVisitReportPdfError(err);
  return resolveVisitReportPdfErrorMessage(err, classification);
}
